/**
 * iter 53 / 63 / 69 — UNIFIED USER MANAGER DRAWER (multi-role + multi-dept).
 *
 * Single canonical drawer for User create + edit. Now supports many-to-many
 * Roles + Departments + Locations, rendered as three mirrored checklists.
 *
 * Props
 * -----
 *   open           bool
 *   onClose        fn()  — called on cancel / backdrop / back-arrow
 *   onSuccess      fn(saved)  — fires after a successful save, BEFORE auto-close
 *   mode           "create" | "edit"
 *   initialData    obj  — user record to hydrate when mode='edit'
 *   orgId          str  — target org for the user
 *   orgName        str  — used in success-toast text
 *   locations      arr  — full org locations list for the multi-select
 *   departments    arr  — full org departments list for the multi-select
 *   roles          arr  — full org roles list (system + custom)  for the multi-select
 *   canDelete      bool — show DELETE button when mode='edit'
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Star, ShieldCheck, ShieldStar, Buildings, ShieldCheck as ShieldCheckLg, UploadSimple } from "@phosphor-icons/react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Drawer, Field } from "../UI";
import {
  ErrorBanner, DrawerFooter, useDrawerForm,
} from "./_managerShared";

// Pretty labels for the seeded system roles. Custom roles use the raw name.
const SYSTEM_ROLE_LABELS = {
  super_admin: "Super Admin",
  admin:       "Admin",
  dispatcher:  "Dispatcher",
  server:      "Process Server",
  accountant:  "Accountant",
  employee:    "Employee",
};

const EMPTY = {
  name: "", email: "", phone: "", mobile_phone: "",
  role_ids: [], department_ids: [], location_ids: [],
  is_notary: false,
  notary_commission_number: "", notary_state: "", notary_expiration_date: "",
  // iter 70.5 — extra notary fields migrated from the retired Your Profile drawer.
  notary_county: "", notary_commissioned_date: "", notary_card_url: "",
};

const hydrate = (rec) => ({
  name: rec.name || "",
  email: rec.email || "",
  phone: rec.phone || "",
  mobile_phone: rec.mobile_phone || "",
  role_ids: rec.role_ids || [],
  department_ids: rec.department_ids || [],
  location_ids: rec.location_ids || [],
  is_notary: !!rec.is_notary,
  notary_commission_number: rec.notary_commission_number || "",
  notary_state: rec.notary_state || "",
  notary_expiration_date: rec.notary_expiration_date || "",
  notary_county: rec.notary_county || "",
  notary_commissioned_date: rec.notary_commissioned_date || "",
  notary_card_url: rec.notary_card_url || "",
});

export default function UserManagerDrawer({
  open, onClose, onSuccess, mode = "create", initialData = null,
  orgId, orgName, locations = [], departments = [], roles = [], canDelete = true,
}) {
  const isEdit = mode === "edit";
  const { user: currentUser, refresh: refreshAuth } = useAuth();
  const f = useDrawerForm({ open, isEdit, initialData, emptyForm: EMPTY, hydrate });
  const { form, setForm, setField, busy, setBusy, err, setErr, requestClose } = f;

  // Caller is a Super Admin in this org? Used to gate the admin/super_admin checkboxes.
  const callerIsSuper = useMemo(() => {
    if (!currentUser) return false;
    if (currentUser.org_id !== orgId) return false;
    // currentUser.role is the denormalised primary role string.
    return currentUser.role === "super_admin";
  }, [currentUser, orgId]);

  // Look up the system "super_admin" + "admin" role IDs (used for gating).
  const SUPER_ROLE_ID = useMemo(
    () => roles.find((r) => r.is_system && r.name === "super_admin")?.id,
    [roles],
  );
  const ADMIN_ROLE_ID = useMemo(
    () => roles.find((r) => r.is_system && r.name === "admin")?.id,
    [roles],
  );

  const toggleId = (key, id) => {
    setForm((cur) => ({
      ...cur,
      [key]: cur[key].includes(id)
        ? cur[key].filter((x) => x !== id)
        : [...cur[key], id],
    }));
  };

  const roleLabel = (r) =>
    r.is_system ? (SYSTEM_ROLE_LABELS[r.name] || r.name) : r.name;

  // Disable super_admin / admin checkboxes for junior callers (mirrors the
  // backend `_enforce_junior_admin_gate` 403). On the create path, the first
  // user always becomes super_admin regardless, so we let them choose freely.
  const isGatedRole = (r) =>
    isEdit
    && !callerIsSuper
    && r.is_system
    && (r.name === "super_admin" || r.name === "admin");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const corePayload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        mobile_phone: form.mobile_phone || null,
        is_notary: !!form.is_notary,
        notary_commission_number: form.is_notary ? (form.notary_commission_number || null) : null,
        notary_state: form.is_notary ? (form.notary_state || null) : null,
        notary_expiration_date: form.is_notary ? (form.notary_expiration_date || null) : null,
        // iter 70.5 — extra notary fields from the retired Your Profile drawer.
        notary_county: form.is_notary ? (form.notary_county || null) : null,
        notary_commissioned_date: form.is_notary ? (form.notary_commissioned_date || null) : null,
        notary_card_url: form.is_notary ? (form.notary_card_url || null) : null,
      };
      let saved;
      if (isEdit) {
        // PATCH core fields; then PUT three M2M sets in parallel.
        const uid = initialData.id;
        const r = await api.patch(`/org-admin/${orgId}/users/${uid}`, corePayload);
        await Promise.all([
          api.put(`/org-admin/${orgId}/users/${uid}/roles`,       { role_ids:       form.role_ids       }),
          api.put(`/org-admin/${orgId}/users/${uid}/departments`, { department_ids: form.department_ids }),
          api.put(`/org-admin/${orgId}/users/${uid}/locations`,   { location_ids:   form.location_ids   }),
        ]);
        saved = r.data;
      } else {
        // CREATE accepts the M2M arrays inline (single round-trip).
        const r = await api.post(`/org-admin/${orgId}/users`, {
          ...corePayload,
          role_ids:       form.role_ids,
          department_ids: form.department_ids,
          location_ids:   form.location_ids,
        });
        saved = r.data;
      }
      toast.success(
        isEdit
          ? `User "${form.name}" updated.`
          : `User "${form.name}" added to ${orgName || "the organization"}.`,
      );
      if (isEdit && currentUser?.id && initialData?.id === currentUser.id) {
        try { await refreshAuth?.(); } catch { /* non-blocking */ }
      }
      onSuccess?.(saved);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !initialData) return;
    if (!confirm(`Delete user "${initialData.name}"? This cannot be undone.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/org-admin/${orgId}/users/${initialData.id}`);
      toast.success(`User "${initialData.name}" deleted.`);
      onSuccess?.(null);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  // For first-user-of-org tip: if drawer is in create mode AND the caller
  // is about to create the very first super_admin, we surface a tip.
  const willBeFirstSuperAdmin = !isEdit && form.role_ids.includes(SUPER_ROLE_ID);

  return (
    <Drawer
      open={open}
      onClose={() => requestClose(onClose)}
      title={isEdit ? "Edit user" : "Add user"}
      size="narrow"
      testid="user-manager-drawer"
      footer={
        <DrawerFooter
          testid="user-manager-footer"
          onCancel={() => requestClose(onClose)}
          onSave={save}
          mode={mode}
          busy={busy}
          saveLabel={isEdit ? "Update user" : "Create user"}
          saveDisabled={!form.name.trim() || !form.email.trim()}
          left={isEdit && canDelete && (
            <button
              type="button"
              className="btn btn-ghost text-destructive"
              onClick={remove}
              disabled={busy}
              data-testid="user-manager-delete-btn"
            >
              DELETE
            </button>
          )}
        />
      }
    >
      <ErrorBanner err={err} testid="user-manager-error" />

      {willBeFirstSuperAdmin && (
        <div className="surface p-2 mb-3 text-[11px] flex items-center gap-2 bg-yellow-50 dark:bg-yellow-950/40 border-yellow-300">
          <Star size={12} weight="fill" className="text-yellow-500" />
          <span>This user will be a Super Admin.</span>
        </div>
      )}

      <Field label="NAME">
        <input className="input" autoFocus value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          data-testid="user-manager-name-input" />
      </Field>
      <Field label="EMAIL">
        <input className="input" type="email" value={form.email}
          onChange={(e) => setField("email", e.target.value)}
          data-testid="user-manager-email-input" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="PHONE">
          <input className="input" value={form.phone}
            onChange={(e) => setField("phone", e.target.value)} />
        </Field>
        <Field label="MOBILE">
          <input className="input" value={form.mobile_phone}
            onChange={(e) => setField("mobile_phone", e.target.value)} />
        </Field>
      </div>

      {/* ROLES — multi-select checklist (mirrors Locations) */}
      <Field label={`ROLES (${form.role_ids.length} selected)`}>
        <div className="surface p-2 max-h-48 overflow-y-auto" data-testid="user-manager-roles-checklist">
          {roles.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No roles available.</div>
          )}
          {roles.map((r) => {
            const checked = form.role_ids.includes(r.id);
            const gated = isGatedRole(r);
            return (
              <label
                key={r.id}
                className={`flex items-center gap-2 py-1 px-1 rounded ${gated ? "opacity-50 cursor-not-allowed" : "hover:bg-muted/40 cursor-pointer"}`}
                title={gated ? "Only Super Admins can grant this role" : ""}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={gated}
                  onChange={() => !gated && toggleId("role_ids", r.id)}
                  data-testid={`user-manager-role-checkbox-${r.name}`}
                />
                <span className="text-sm flex-1 truncate flex items-center gap-1">
                  {r.is_system && r.name === "super_admin" && (
                    <ShieldStar size={11} weight="fill" className="text-yellow-500" />
                  )}
                  {r.is_system && r.name === "admin" && (
                    <ShieldCheck size={11} weight="fill" className="text-primary" />
                  )}
                  {roleLabel(r)}
                </span>
                <span className={`text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded ${r.is_system ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {r.is_system ? "SYSTEM" : "CUSTOM"}
                </span>
              </label>
            );
          })}
        </div>
      </Field>

      {/* DEPARTMENTS — multi-select checklist (mirrors Locations) */}
      <Field label={`DEPARTMENTS (${form.department_ids.length} selected)`}>
        <div className="surface p-2 max-h-48 overflow-y-auto" data-testid="user-manager-departments-checklist">
          {departments.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No departments available. Add one first.</div>
          )}
          {departments.map((d) => (
            <label key={d.id} className="flex items-center gap-2 py-1 px-1 hover:bg-muted/40 cursor-pointer rounded">
              <input
                type="checkbox"
                checked={form.department_ids.includes(d.id)}
                onChange={() => toggleId("department_ids", d.id)}
                data-testid={`user-manager-department-checkbox-${d.id}`}
              />
              <span className="text-sm flex-1 truncate flex items-center gap-1">
                <Buildings size={10} className="text-muted-foreground" />
                {d.name}
              </span>
              {d.description && (
                <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">
                  {d.description}
                </span>
              )}
            </label>
          ))}
        </div>
      </Field>

      {/* LOCATIONS — multi-select checklist (the original; unchanged structure) */}
      <Field label={`LOCATIONS (${form.location_ids.length} selected)`}>
        <div className="surface p-2 max-h-48 overflow-y-auto" data-testid="user-manager-locations-checklist">
          {locations.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No locations available. Add one first.</div>
          )}
          {locations.map((l) => (
            <label key={l.id} className="flex items-center gap-2 py-1 px-1 hover:bg-muted/40 cursor-pointer rounded">
              <input
                type="checkbox"
                checked={form.location_ids.includes(l.id)}
                onChange={() => toggleId("location_ids", l.id)}
                data-testid={`user-manager-location-checkbox-${l.id}`}
              />
              <span className="text-sm flex-1 truncate flex items-center gap-1">
                {l.is_main_location && <Star size={10} weight="fill" className="text-primary" />}
                {l.name}
              </span>
              <span className="text-[10px] text-muted-foreground truncate">
                {[l.city, l.state].filter(Boolean).join(", ")}
              </span>
            </label>
          ))}
        </div>
      </Field>
      {/* NOTARY CREDENTIALS — collapsible, only the toggle + commission # / state / expiry */}
      <Field label="NOTARY">
        <label className="flex items-center gap-2 cursor-pointer py-1">
          <input
            type="checkbox"
            checked={!!form.is_notary}
            onChange={(e) => setField("is_notary", e.target.checked)}
            data-testid="user-manager-is-notary-toggle"
          />
          <ShieldCheckLg size={12} className="text-green-600" />
          <span className="text-sm">This user is a notary</span>
        </label>
        {form.is_notary && (
          <div className="surface p-3 mt-2 space-y-2" data-testid="user-manager-notary-fields">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mb-1">COMMISSION #</div>
                <input
                  className="input"
                  value={form.notary_commission_number}
                  onChange={(e) => setField("notary_commission_number", e.target.value)}
                  placeholder="e.g. 12345678"
                  data-testid="user-manager-notary-commission-input"
                />
              </div>
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mb-1">STATE</div>
                <input
                  className="input"
                  value={form.notary_state}
                  maxLength={2}
                  onChange={(e) => setField("notary_state", e.target.value.toUpperCase())}
                  placeholder="MI"
                  data-testid="user-manager-notary-state-input"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mb-1">COUNTY</div>
              <input
                className="input"
                value={form.notary_county}
                onChange={(e) => setField("notary_county", e.target.value)}
                placeholder="e.g. Travis"
                data-testid="user-manager-notary-county-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mb-1">COMMISSIONED</div>
                <input
                  className="input"
                  type="date"
                  value={form.notary_commissioned_date}
                  onChange={(e) => setField("notary_commissioned_date", e.target.value)}
                  data-testid="user-manager-notary-commissioned-input"
                />
              </div>
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mb-1">COMMISSION EXPIRATION</div>
                <input
                  className="input"
                  type="date"
                  value={form.notary_expiration_date}
                  onChange={(e) => setField("notary_expiration_date", e.target.value)}
                  data-testid="user-manager-notary-expiry-input"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mb-1">NOTARY CARD</div>
              <NotaryCardUpload
                url={form.notary_card_url}
                onUploaded={(u) => setField("notary_card_url", u)}
              />
            </div>
          </div>
        )}
      </Field>
    </Drawer>
  );
}


/**
 * Small file-upload widget for the notary card (PDF or image). Posts to the
 * `/uploads/user/notary-card` endpoint which proxies to Supabase Storage and
 * returns a public URL. We don't auto-save the user — the drawer's SAVE
 * button persists the URL like any other field.
 */
function NotaryCardUpload({ url, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/user/notary-card", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.url) onUploaded?.(r.data.url);
      else setErr("Upload succeeded but no URL returned.");
    } catch (ex) { setErr(formatErr(ex)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="user-manager-notary-card-upload">
      <div className="flex items-center gap-2">
        <label className="btn btn-secondary btn-sm cursor-pointer">
          <UploadSimple size={12} />
          {busy ? "UPLOADING…" : (url ? "REPLACE FILE" : "CHOOSE FILE")}
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={onPick}
            className="hidden"
            data-testid="user-manager-notary-card-upload-input"
            disabled={busy}
          />
        </label>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-muted-foreground underline truncate max-w-xs"
            data-testid="user-manager-notary-card-upload-link"
          >
            {url.split("/").pop()}
          </a>
        )}
      </div>
      {err && (
        <div className="text-xs text-destructive mt-1" data-testid="user-manager-notary-card-upload-error">
          {err}
        </div>
      )}
    </div>
  );
}
